import { useShellDark } from '../../lib/useShellDark';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiErrorDetail } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import SiteTimeline from '@/components/developer/SiteTimeline';
import ShareLinksPanel from '@/components/developer/ShareLinksPanel';
import ProgressReportsPanel from '@/components/developer/ProgressReportsPanel';
import CaptureImportsPanel from '@/components/developer/CaptureImportsPanel';
import ProcorePanel from '@/components/developer/ProcorePanel';
import InfoTip from '@/components/ui/info-tip';
import { propertyCopy, isPropertyKind } from '@/lib/clientTypes';
import { Page, PageHeader, List, Row, Empty, Section, Loading, humanize } from '@/components/calm/Calm';

export const isPropertySide = (kind) => isPropertyKind(kind) && kind !== 'builder';

const inputStyle = {
  padding: '10px 12px',
  border: '1px solid var(--c-line)',
  background: 'var(--c-field)',
  color: 'var(--c-ink)',
  fontFamily: 'inherit',
  borderRadius: 2,
  fontSize: 14,
  // Flex rows must be able to shrink inputs on a phone instead of overflowing.
  minWidth: 0,
  maxWidth: '100%',
  boxSizing: 'border-box',
};

// Colours come from the .bx-solid class (index.css) so the button inverts in dark
// mode and a disabled button stays an outlined, readable block.
const buttonStyle = {
  alignSelf: 'flex-start',
  padding: '10px 18px',
  borderRadius: 2,
  fontSize: 13,
  cursor: 'pointer',
};

// The timeline with one map file's saved figures replaced.
function withMeasures(tl, did, list) {
  if (!tl) return tl;
  return { ...tl, visits: (tl.visits || []).map((v) => ({ ...v, deliverables: (v.deliverables || []).map((d) => (String(d.id) === String(did) ? { ...d, measures: list } : d)) })) };
}

const CGI_ASSET_STATUS_COLOR = {
  queued: 'var(--c-ink-mute)',
  rendering: 'var(--c-ink-mute)',
  complete: 'var(--c-ink-mute)',
  failed: 'var(--c-bad)',
};

// MVP: list a developer's projects + create a new one, and (2026-09-13) drill
// into one project to set its site address, add phases, schedule real
// progress-capture visits against Dispatch's vendor network, and queue CGI
// renders for phases that aren't built yet — both halves of this vertical
// now have a UI, not just an API. Lot/amenity pin placement on the site plan
// image and the public microsite still land in follow-up work.
export default function DeveloperProjectsPage() {
  const { user: me } = useAuth();
  const copy = propertyCopy(me?.account_type);
  const navigate = useNavigate();
  // Property managers, commercial landlords, investors and REITs open the
  // property page (unit board, spaces, baseline), not the builder site card.
  const propertySide = isPropertySide(me?.account_type);
  const [projects, setProjects] = useState(null);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  // ?pick=book (from "Book" with several properties): a row opens that
  // property's booking dialog.
  const [pickBook, setPickBook] = useState(false);
  // Set once on mount from a post-Stripe-Checkout redirect (?dev_session_id=&dev_project_id=)
  // so BillingSection knows to poll instead of waiting for the webhook.
  const [pendingCheckoutSessionId, setPendingCheckoutSessionId] = useState(null);

  async function loadProjects() {
    try {
      const res = await api.get('/dev/projects');
      setProjects(res.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Failed to load projects');
    }
  }

  useEffect(() => {
    loadProjects();
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('dev_session_id');
    const projectId = params.get('dev_project_id');
    if (params.get('pick') === 'book') setPickBook(true);
    const manageId = params.get('manage');
    if (manageId) {
      setSelectedId(manageId);
      params.delete('manage');
      const rest = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
    if (sessionId && projectId) {
      setSelectedId(projectId);
      setPendingCheckoutSessionId(sessionId);
      params.delete('dev_session_id');
      params.delete('dev_project_id');
      const rest = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
  }, []);

  async function handleCreate(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    setError('');
    try {
      await api.post('/dev/projects', { name: name.trim(), description: description.trim() || null });
      setName('');
      setDescription('');
      await loadProjects();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  }

  return (
    <Page testid="developer-projects" width={760}>
      <PageHeader title={copy.things} subtitle={propertySide && pickBook ? `Pick the ${copy.thing} to book.` : projects && projects.length ? `${projects.length} ${projects.length === 1 ? copy.thing : copy.things.toLowerCase()}` : `Every ${copy.thing} in one place.`} />

      {projects === null ? <Loading /> : projects.length === 0 ? (
        <Empty>{`No ${copy.things.toLowerCase()} yet. Name your first ${copy.thing} below.`}</Empty>
      ) : (
        <List testid="developer-project-list">
          {projects.map((p) => (
            <div key={p.id}>
              <Row testid="developer-project-row" wide={false}
                onClick={() => (propertySide && selectedId !== p.id ? navigate(`/developer/property/${p.id}${pickBook ? '?book=1' : ''}`) : setSelectedId(selectedId === p.id ? null : p.id))}
                title={p.name}
                meta={p.description ? [p.description] : null}
                status={humanize(p.status)}
                aside={<span style={{ fontSize: 13, color: 'var(--c-ink-soft)', textDecoration: 'underline', textUnderlineOffset: 3, textDecorationColor: 'var(--c-line)' }}>{selectedId === p.id ? 'Hide' : propertySide ? (pickBook ? 'Book' : 'Open') : 'Manage'}</span>} />
              {selectedId === p.id ? (
                <ProjectDetail
                  project={p}
                  propertySide={propertySide}
                  onProjectUpdated={loadProjects}
                  pendingCheckoutSessionId={pendingCheckoutSessionId}
                  onCheckoutSessionConsumed={() => setPendingCheckoutSessionId(null)}
                />
              ) : null}
            </div>
          ))}
        </List>
      )}

      <Section title={`New ${copy.thing}`}>
        <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 520 }}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={copy.namePlaceholder}
            style={inputStyle}
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (optional)"
            rows={2}
            style={inputStyle}
          />
          <button type="submit" disabled={creating || !name.trim()} className="bx-solid" style={{ ...buttonStyle, cursor: creating ? 'wait' : 'pointer' }}>
            {creating ? 'Creating…' : `Create ${copy.thing}`}
          </button>
          {error ? <p style={{ color: 'var(--c-bad)', fontSize: 13 }}>{error}</p> : null}
        </form>
      </Section>
    </Page>
  );
}

function ProjectDetail({ project, propertySide = false, onProjectUpdated, pendingCheckoutSessionId, onCheckoutSessionConsumed }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');

  async function loadDetail() {
    try {
      const res = await api.get(`/dev/projects/${project.id}`);
      setDetail(res.data);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Failed to load project');
    }
  }

  useEffect(() => {
    loadDetail();
  }, [project.id]);

  return (
    <div style={{ padding: '20px 4px 28px', borderBottom: '1px solid var(--c-hair-s)', display: 'flex', flexDirection: 'column', gap: 28 }}>
      {error ? <p style={{ color: 'var(--c-bad)', fontSize: 13 }}>{error}</p> : null}
      {detail === null ? (
        <p style={{ fontSize: 13, color: 'var(--c-ink-mute)' }}>Loading…</p>
      ) : (
        <>
          <BillingSection
            project={detail}
            pendingCheckoutSessionId={pendingCheckoutSessionId}
            onCheckoutSessionConsumed={onCheckoutSessionConsumed}
            onSubscriptionChanged={async () => {
              await loadDetail();
              await onProjectUpdated();
            }}
          />
          <AddressForm
            project={detail}
            propertySide={propertySide}
            onSaved={async () => {
              await loadDetail();
              await onProjectUpdated();
            }}
          />
          {propertySide ? null : <PhasesSection project={detail} onPhasesChanged={loadDetail} />}
          <LotsSection project={detail} onLotsChanged={loadDetail} />
          {propertySide ? null : (
            <>
              <AmenitiesSection project={detail} onAmenitiesChanged={loadDetail} />
              <CaptureVisitsSection project={detail} />
              <CgiAssetsSection project={detail} />
            </>
          )}
        </>
      )}
    </div>
  );
}

const SUBSCRIPTION_STATUS_COLOR = {
  inactive: 'var(--c-ink-mute)',
  active: 'var(--c-ink-mute)',
  canceled: 'var(--c-bad)',
};

function formatCadCents(cents) {
  return `$${(cents / 100).toFixed(0)}`;
}

// "Portfolio watch, quarterly, $195 CAD per property a month, billed $585 a quarter"
function planLabel(t) {
  const base = `${t.name}, ${formatCadCents(t.price_cad_cents)} CAD`;
  if (!t.client) return `${base} per site a month`;
  const per = (t.unit || '').includes('property') ? 'per property a month' : 'a month';
  const billed = t.interval_count && t.interval_count > 1 && t.billing_label
    ? `, ${t.billing_label.charAt(0).toLowerCase()}${t.billing_label.slice(1)}` : '';
  return `${base} ${per}${billed}`;
}

function BillingSection({ project, pendingCheckoutSessionId, onCheckoutSessionConsumed, onSubscriptionChanged }) {
  const [tiers, setTiers] = useState(null);
  const [selectedTier, setSelectedTier] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [managing, setManaging] = useState(false);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState('');
  const lotCount = (project.lots || []).length;

  const { user } = useAuth();
  const accountType = user?.account_type || 'builder';

  async function loadTiers() {
    try {
      const [pricing, catalog] = await Promise.all([
        api.get('/dev/pricing'),
        api.get('/dev/catalog', { params: { type: accountType } }).catch(() => null),
      ]);
      // Site progress plans come from /dev/pricing; per-property plans for
      // property managers, commercial and investors come from the catalog
      // (bookable once their prices are approved).
      const clientPlans = [];
      for (const f of catalog?.data?.families || []) {
        if (f.key === 'site_progress') continue;
        for (const i of f.items || []) {
          if (i.kind !== 'plan' || i.price_cad_cents == null) continue;
          clientPlans.push({ ...i, client: true, available: i.bookable === 'checkout' });
        }
      }
      setTiers([...clientPlans, ...(pricing.data || [])]);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Failed to load pricing');
    }
  }

  useEffect(() => {
    loadTiers();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!tiers || tiers.length === 0) return;
    const suggested = tiers.find((t) => t.client && t.available) || tiers.find((t) => t.key === 'visual_monthly') || tiers[0];
    setSelectedTier((prev) => prev || suggested.key);
  }, [tiers, lotCount]);

  useEffect(() => {
    if (!pendingCheckoutSessionId) return;
    let cancelled = false;
    setPolling(true);
    (async () => {
      try {
        await api.get(`/dev/projects/${project.id}/subscription/status`, {
          params: { session_id: pendingCheckoutSessionId },
        });
        if (!cancelled) await onSubscriptionChanged();
      } catch (e) {
        if (!cancelled) setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Failed to confirm checkout');
      } finally {
        if (!cancelled) {
          setPolling(false);
          onCheckoutSessionConsumed();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [pendingCheckoutSessionId]);

  async function handleSubscribe(e) {
    e.preventDefault();
    if (!selectedTier) return;
    setSubscribing(true);
    setError('');
    try {
      const res = await api.post(`/dev/projects/${project.id}/subscribe`, { tier_key: selectedTier });
      window.location.href = res.data.url;
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Failed to start checkout');
      setSubscribing(false);
    }
  }

  async function handleManage() {
    setManaging(true);
    setError('');
    try {
      const res = await api.post(`/dev/projects/${project.id}/subscription/disable`, {});
      window.location.href = res.data.url;
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Failed to open billing portal');
      setManaging(false);
    }
  }

  const status = project.subscription_status || 'inactive';
  const currentTier = tiers && (tiers.find((t) => t.key === project.subscription_tier) || null);
  const hasClientPlans = !!(tiers && tiers.some((t) => t.client));

  return (
    <section>
      <h2 style={{ fontSize: 16, fontWeight: 500, letterSpacing: -0.2, marginBottom: 4 }}>Billing</h2>
      <p style={{ fontSize: 12, color: 'var(--c-ink-mute)', marginTop: 0, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
        {hasClientPlans ? 'Billed per property.' : 'Billed monthly per site.'}
        <InfoTip label="About site plans">Pick the capture type and how often a pilot flies the site. Mapped plans add a one-time first-capture setup (ground control).{status !== 'active' ? ' The public project page (once live) is only reachable while the subscription is active.' : ''}</InfoTip>
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ color: SUBSCRIPTION_STATUS_COLOR[status] || 'var(--c-ink-mute)', fontWeight: 500, fontSize: 13 }}>
          {humanize(status)}
        </span>
        {status === 'active' && currentTier ? (
          <span style={{ fontSize: 13, color: 'var(--c-ink-mute)' }}>
            {planLabel(currentTier)}
          </span>
        ) : null}
      </div>

      {polling ? <p style={{ fontSize: 13, color: 'var(--c-ink-mute)' }}>Confirming checkout…</p> : null}

      {status === 'active' ? (
        <button
          type="button"
          onClick={handleManage}
          disabled={managing}
          className="bx-solid" style={{ ...buttonStyle, cursor: managing ? 'wait' : 'pointer' }}
        >
          {managing ? 'Opening…' : 'Manage billing'}
        </button>
      ) : tiers === null ? (
        <p style={{ fontSize: 13, color: 'var(--c-ink-mute)' }}>Loading pricing…</p>
      ) : (
        <form onSubmit={handleSubscribe} style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 420 }}>
          <select value={selectedTier} onChange={(e) => setSelectedTier(e.target.value)} style={inputStyle}>
            {tiers.map((t) => (
              <option key={t.key} value={t.key} disabled={!t.available}>
                {planLabel(t)}
                {t.setup_cad_cents ? ` + ${formatCadCents(t.setup_cad_cents)} setup` : ''}
                {t.available ? '' : ', not yet available'}
              </option>
            ))}
          </select>
          <button
            type="submit"
            disabled={subscribing || !selectedTier || !(tiers.find((t) => t.key === selectedTier) || {}).available}
            className="bx-solid" style={{ ...buttonStyle, cursor: subscribing ? 'wait' : 'pointer' }}
          >
            {subscribing ? 'Starting checkout…' : 'Subscribe'}
          </button>
        </form>
      )}
      {error ? <p style={{ color: 'var(--c-bad)', fontSize: 13, marginTop: 8 }}>{error}</p> : null}
    </section>
  );
}

function AddressForm({ project, propertySide = false, onSaved }) {
  const [address, setAddress] = useState(project.address || '');
  const [city, setCity] = useState(project.city || '');
  const [lat, setLat] = useState(project.lat ?? '');
  const [lng, setLng] = useState(project.lng ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSave(e) {
    e.preventDefault();
    if (!address.trim()) return;
    setSaving(true);
    setError('');
    try {
      await api.patch(`/dev/projects/${project.id}`, {
        address: address.trim(),
        city: city.trim() || null,
        lat: lat === '' ? null : Number(lat),
        lng: lng === '' ? null : Number(lng),
      });
      await onSaved();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Failed to save address');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section>
      <h2 style={{ fontSize: 16, fontWeight: 500, letterSpacing: -0.2, marginBottom: 4 }}>{propertySide ? 'Address' : 'Site address'}</h2>
      <p style={{ fontSize: 12, color: 'var(--c-ink-mute)', marginTop: 0, marginBottom: 8 }}>
        {propertySide ? 'Where we go for visits. Needed to book one.' : 'Where the pilot goes. Needed to schedule a visit.'}
      </p>
      <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 420 }}>
        <input
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="Street address"
          style={inputStyle}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" style={{ ...inputStyle, flex: '1 1 160px' }} />
          <input
            value={lat}
            onChange={(e) => setLat(e.target.value)}
            placeholder="Lat (optional)"
            inputMode="decimal"
            style={{ ...inputStyle, width: 120 }}
          />
          <input
            value={lng}
            onChange={(e) => setLng(e.target.value)}
            placeholder="Lng (optional)"
            inputMode="decimal"
            style={{ ...inputStyle, width: 120 }}
          />
        </div>
        <button type="submit" disabled={saving || !address.trim()} className="bx-solid" style={{ ...buttonStyle, cursor: saving ? 'wait' : 'pointer' }}>
          {saving ? 'Saving…' : 'Save address'}
        </button>
        {error ? <p style={{ color: 'var(--c-bad)', fontSize: 13 }}>{error}</p> : null}
      </form>
    </section>
  );
}

function PhasesSection({ project, onPhasesChanged }) {
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const phases = project.phases || [];

  async function handleCreate(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    setError('');
    try {
      await api.post(`/dev/projects/${project.id}/phases`, { name: name.trim() });
      setName('');
      await onPhasesChanged();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Failed to create phase');
    } finally {
      setCreating(false);
    }
  }

  return (
    <section>
      <h2 style={{ fontSize: 16, fontWeight: 500, letterSpacing: -0.2, marginBottom: 8 }}>Phases</h2>
      {phases.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--c-ink-mute)', marginTop: 0 }}>No phases yet.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {phases.map((ph) => (
            <li key={ph.id} style={{ fontSize: 13 }}>
              {ph.name}
              <span style={{ marginLeft: 8, fontSize: 12.5, color: 'var(--c-ink-mute)' }}>{humanize(ph.status)}</span>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={handleCreate} style={{ display: 'flex', gap: 8, maxWidth: 420 }}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New phase name (e.g. Phase 1)"
          style={{ ...inputStyle, flex: 1 }}
        />
        <button type="submit" disabled={creating || !name.trim()} className="bx-solid" style={{ ...buttonStyle, cursor: creating ? 'wait' : 'pointer' }}>
          {creating ? 'Adding…' : 'Add phase'}
        </button>
      </form>
      {error ? <p style={{ color: 'var(--c-bad)', fontSize: 13 }}>{error}</p> : null}
    </section>
  );
}

const LOT_STATUS_COLOR = {
  available: 'var(--c-ink-mute)',
  reserved: 'var(--c-ink-mute)',
  sold: 'var(--c-ink-mute)',
};

function LotsSection({ project, onLotsChanged }) {
  const { user } = useAuth();
  // Same underlying dev_lots data for both account types — builder sells
  // lots one at a time (needs to manage every row), reit_leasing cares
  // about what's currently available across many units (needs to filter).
  // Terminology follows the account type: a builder's "lot" is a REIT's "unit".
  // Every non-builder property type works in units (commercial: spaces).
  const isReit = isPropertyKind(user?.account_type) && user?.account_type !== 'builder';
  const noun = propertyCopy(user?.account_type).units;
  const nounSingular = propertyCopy(user?.account_type).unit;

  const [lotNumber, setLotNumber] = useState('');
  const [phaseId, setPhaseId] = useState('');
  const [priceCents, setPriceCents] = useState('');
  const [sqft, setSqft] = useState('');
  const [creating, setCreating] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');

  const phases = project.phases || [];
  const lots = project.lots || [];
  const visibleLots = statusFilter === 'all' ? lots : lots.filter((l) => l.status === statusFilter);

  async function handleCreate(e) {
    e.preventDefault();
    if (!lotNumber.trim()) return;
    setCreating(true);
    setError('');
    try {
      await api.post(`/dev/projects/${project.id}/lots`, {
        lot_number: lotNumber.trim(),
        phase_id: phaseId || null,
        price_cents: priceCents === '' ? null : Math.round(Number(priceCents) * 100),
        sqft: sqft === '' ? null : Number(sqft),
      });
      setLotNumber('');
      setPriceCents('');
      setSqft('');
      await onLotsChanged();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || `Failed to create ${nounSingular}`);
    } finally {
      setCreating(false);
    }
  }

  async function handleStatusChange(lotId, status) {
    setSavingId(lotId);
    setError('');
    try {
      await api.patch(`/dev/lots/${lotId}`, { status });
      await onLotsChanged();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || `Failed to update ${nounSingular}`);
    } finally {
      setSavingId(null);
    }
  }

  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ fontSize: 16, fontWeight: 500, letterSpacing: -0.2, margin: 0 }}>{noun}</h2>
        {isReit && lots.length > 0 ? (
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ ...inputStyle, padding: '6px 10px', fontSize: 12 }}>
            <option value="all">All statuses</option>
            <option value="available">Available</option>
            <option value="reserved">Reserved</option>
            <option value="sold">Leased/Sold</option>
          </select>
        ) : null}
      </div>

      {lots.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--c-ink-mute)', marginTop: 0 }}>No {noun.toLowerCase()} yet.</p>
      ) : visibleLots.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--c-ink-mute)', marginTop: 0 }}>No {noun.toLowerCase()} match this filter.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {visibleLots.map((l) => (
            <li key={l.id} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
              <strong>{l.lot_number}</strong>
              {l.phase_id ? (
                <span style={{ color: 'var(--c-ink-mute)' }}>{(phases.find((ph) => ph.id === l.phase_id) || {}).name}</span>
              ) : null}
              {l.price_cents != null ? <span style={{ color: 'var(--c-ink-mute)' }}>${(l.price_cents / 100).toLocaleString()}</span> : null}
              {l.sqft != null ? <span style={{ color: 'var(--c-ink-mute)' }}>{l.sqft.toLocaleString()} sqft</span> : null}
              <select
                value={l.status}
                onChange={(e) => handleStatusChange(l.id, e.target.value)}
                disabled={savingId === l.id}
                style={{
                  marginLeft: 'auto', fontSize: 12.5, fontWeight: 400,
                  color: LOT_STATUS_COLOR[l.status] || 'var(--c-ink-mute)', border: '1px solid var(--c-line)',
                  borderRadius: 2, padding: '2px 6px', background: 'none',
                }}
              >
                <option value="available">Available</option>
                <option value="reserved">Reserved</option>
                <option value="sold">{isReit ? 'Leased' : 'Sold'}</option>
              </select>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 420 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={lotNumber}
            onChange={(e) => setLotNumber(e.target.value)}
            placeholder={isReit ? `${nounSingular[0].toUpperCase()}${nounSingular.slice(1)} # (e.g. 1204)` : 'Lot # (e.g. 17)'}
            style={{ ...inputStyle, flex: 1 }}
          />
          <select value={phaseId} onChange={(e) => setPhaseId(e.target.value)} style={{ ...inputStyle, flex: 1 }}>
            <option value="">No phase</option>
            {phases.map((ph) => (
              <option key={ph.id} value={ph.id}>{ph.name}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={priceCents}
            onChange={(e) => setPriceCents(e.target.value)}
            placeholder={isReit ? 'Monthly rent ($)' : 'Price ($)'}
            inputMode="decimal"
            style={{ ...inputStyle, flex: 1 }}
          />
          <input
            value={sqft}
            onChange={(e) => setSqft(e.target.value)}
            placeholder="Sqft"
            inputMode="numeric"
            style={{ ...inputStyle, flex: 1 }}
          />
        </div>
        <button type="submit" disabled={creating || !lotNumber.trim()} className="bx-solid" style={{ ...buttonStyle, cursor: creating ? 'wait' : 'pointer' }}>
          {creating ? 'Adding…' : `Add ${nounSingular}`}
        </button>
      </form>
      {error ? <p style={{ color: 'var(--c-bad)', fontSize: 13, marginTop: 8 }}>{error}</p> : null}
      {isReit ? (
        <p style={{ fontSize: 11, color: 'var(--c-ink-mute)', marginTop: 8 }}>
          Update leasing status here.
        </p>
      ) : null}
    </section>
  );
}

function AmenitiesSection({ project, onAmenitiesChanged }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const amenities = project.amenities || [];

  async function handleCreate(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    setError('');
    try {
      await api.post(`/dev/projects/${project.id}/amenities`, {
        name: name.trim(),
        description: description.trim() || null,
      });
      setName('');
      setDescription('');
      await onAmenitiesChanged();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Failed to create amenity');
    } finally {
      setCreating(false);
    }
  }

  return (
    <section>
      <h2 style={{ fontSize: 16, fontWeight: 500, letterSpacing: -0.2, marginBottom: 8 }}>Amenities</h2>
      {amenities.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--c-ink-mute)', marginTop: 0 }}>No amenities yet.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {amenities.map((a) => (
            <li key={a.id} style={{ fontSize: 13 }}>
              <strong>{a.name}</strong>
              {a.description ? <span style={{ color: 'var(--c-ink-mute)' }}>, {a.description}</span> : null}
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={handleCreate} style={{ display: 'flex', gap: 8, maxWidth: 420 }}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Amenity name (e.g. Rooftop terrace)"
          style={{ ...inputStyle, flex: 1 }}
        />
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          style={{ ...inputStyle, flex: 1 }}
        />
        <button type="submit" disabled={creating || !name.trim()} className="bx-solid" style={{ ...buttonStyle, cursor: creating ? 'wait' : 'pointer' }}>
          {creating ? 'Adding…' : 'Add'}
        </button>
      </form>
      {error ? <p style={{ color: 'var(--c-bad)', fontSize: 13, marginTop: 8 }}>{error}</p> : null}
    </section>
  );
}

function CaptureVisitsSection({ project }) {
  const dark = useShellDark();
  const [visits, setVisits] = useState(null);
  // A GeoTIFF map with no uploaded preview: the server draws it for measuring.
  const loadMapPreview = useCallback(async (did) => {
    try {
      const r = await api.get(`/dev/projects/${project.id}/deliverables/${did}/map-preview`);
      return (r.data && r.data.src) || null;
    } catch (ex) {
      throw new Error(formatApiErrorDetail(ex?.response?.data?.detail) || 'Couldn’t draw this map right now. Try again.');
    }
  }, [project.id]);
  const [phaseId, setPhaseId] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [notes, setNotes] = useState('');
  const [kind, setKind] = useState('visual');
  const [minInsurance, setMinInsurance] = useState('2000000');
  const [firstMapped, setFirstMapped] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [error, setError] = useState('');
  const phases = project.phases || [];
  const hasAddress = Boolean(project.address);

  async function loadVisits() {
    try {
      const res = await api.get(`/dev/projects/${project.id}/timeline`);
      setVisits(res.data || { visits: [] });
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Failed to load capture visits');
    }
  }

  useEffect(() => {
    loadVisits();
  }, [project.id]);

  async function handleSchedule(e) {
    e.preventDefault();
    setScheduling(true);
    setError('');
    try {
      await api.post(`/dev/projects/${project.id}/capture-visits`, {
        phase_id: phaseId || null,
        scheduled_date: scheduledDate || null,
        notes: notes.trim() || null,
        kind,
        min_insurance_cad: Number(minInsurance) || null,
        first_mapped_capture: kind === 'mapped' ? firstMapped : false,
      });
      setScheduledDate('');
      setNotes('');
      await loadVisits();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Failed to schedule capture visit');
    } finally {
      setScheduling(false);
    }
  }

  return (
    <section>
      <h2 style={{ fontSize: 16, fontWeight: 500, letterSpacing: -0.2, marginBottom: 4 }}>Site visits</h2>
      <p style={{ fontSize: 12, color: 'var(--c-ink-mute)', marginTop: 0, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
        Every visit lands on a dated timeline.
        <InfoTip label="About site visits">A certified drone pilot with verified liability insurance and an Advanced RPAS certificate. Visual: aerial stills and flyover video. Mapped: orthomosaic, 3D model and point cloud. Compare any two dates.</InfoTip>
      </p>

      {visits === null ? (
        <p style={{ fontSize: 13, color: 'var(--c-ink-mute)' }}>Loading…</p>
      ) : (
        <SiteTimeline tone={dark ? 'dark' : 'light'} timeline={visits} phases={phases} project={project} sampleElevation={async (id, x, y) => {
          try {
            const r = await api.get(`/dev/projects/${project.id}/deliverables/${id}/elevation`, { params: { x, y } });
            return r.data;
          } catch (ex) {
            throw new Error(formatApiErrorDetail(ex?.response?.data?.detail) || "Couldn't read the elevation.");
          }
        }} loadMapPreview={loadMapPreview} onSaveNote={async (visitId, text) => {
          try {
            const r = await api.put(`/dev/projects/${project.id}/visits/${visitId}/progress-note`, { text });
            const note = (r.data && r.data.progress_note) || null;
            setVisits((tl) => ({ ...tl, visits: (tl.visits || []).map((x) => (x.id === visitId ? { ...x, progress_note: note } : x)) }));
          } catch (ex) {
            throw new Error(formatApiErrorDetail(ex?.response?.data?.detail) || 'Couldn’t save the note. Try again.');
          }
        }} onSaveMeasure={async (did, body) => {
          try {
            const r = await api.post(`/dev/projects/${project.id}/deliverables/${did}/measurements`, body);
            const list = (r.data && r.data.measures) || [];
            setVisits((tl) => withMeasures(tl, did, list));
            return list;
          } catch (ex) {
            throw new Error(formatApiErrorDetail(ex?.response?.data?.detail) || 'Couldn’t save the figure; nothing was saved.');
          }
        }} onRemoveMeasure={async (did, mid) => {
          try {
            const r = await api.delete(`/dev/projects/${project.id}/deliverables/${did}/measurements/${mid}`);
            const list = (r.data && r.data.measures) || [];
            setVisits((tl) => withMeasures(tl, did, list));
            return list;
          } catch (ex) {
            throw new Error(formatApiErrorDetail(ex?.response?.data?.detail) || 'Couldn’t remove the figure.');
          }
        }} />
      )}

      <CaptureImportsPanel projectId={project.id} phases={phases} onImported={loadVisits} />

      <ProgressReportsPanel projectId={project.id} timeline={visits} />

      <ProgressReportsPanel projectId={project.id} timeline={visits} kind="investor" />

      <ProcorePanel projectId={project.id} />

      <ShareLinksPanel projectId={project.id} />

      {!hasAddress ? (
        <p style={{ fontSize: 12, color: 'var(--c-bad)' }}>Add a site address to schedule a visit.</p>
      ) : (
        <form onSubmit={handleSchedule} style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 420 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <select value={phaseId} onChange={(e) => setPhaseId(e.target.value)} style={{ ...inputStyle, flex: '1 1 160px' }}>
              <option value="">No specific phase</option>
              {phases.map((ph) => (
                <option key={ph.id} value={ph.id}>{ph.name}</option>
              ))}
            </select>
            <input
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              style={{ ...inputStyle, width: 160 }}
            />
          </div>
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes for the pilot (optional)"
            style={inputStyle}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <select value={kind} onChange={(e) => setKind(e.target.value)} style={{ ...inputStyle, flex: '1 1 180px' }} aria-label="Capture type">
              <option value="visual">Visual (stills + video)</option>
              <option value="mapped">Mapped (orthomosaic + 3D)</option>
            </select>
            <select value={minInsurance} onChange={(e) => setMinInsurance(e.target.value)} style={{ ...inputStyle, width: 200 }} aria-label="Pilot insurance minimum">
              <option value="2000000">Pilot liability $2M</option>
              <option value="5000000">Pilot liability $5M (CCDC 41)</option>
            </select>
          </div>
          {kind === 'mapped' ? (
            <label style={{ fontSize: 12, color: 'var(--c-ink-mute)', display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={firstMapped} onChange={(e) => setFirstMapped(e.target.checked)} />
              First mapped capture (sets ground control)
            </label>
          ) : null}
          <button type="submit" disabled={scheduling} className="bx-solid" style={{ ...buttonStyle, cursor: scheduling ? 'wait' : 'pointer' }}>
            {scheduling ? 'Scheduling…' : 'Schedule visit'}
          </button>
        </form>
      )}
      {error ? <p style={{ color: 'var(--c-bad)', fontSize: 13, marginTop: 8 }}>{error}</p> : null}
    </section>
  );
}

function CgiAssetsSection({ project }) {
  const [assets, setAssets] = useState(null);
  const [phaseId, setPhaseId] = useState('');
  const [sourcePlanUrl, setSourcePlanUrl] = useState('');
  const [brief, setBrief] = useState('');
  const [queuing, setQueuing] = useState(false);
  const [error, setError] = useState('');
  const phases = project.phases || [];

  async function loadAssets() {
    try {
      const res = await api.get(`/dev/projects/${project.id}/cgi-assets`);
      setAssets(res.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Failed to load CGI renders');
    }
  }

  useEffect(() => {
    loadAssets();
  }, [project.id]);

  async function handleQueue(e) {
    e.preventDefault();
    if (!phaseId || !sourcePlanUrl.trim()) return;
    setQueuing(true);
    setError('');
    try {
      await api.post(`/dev/projects/${project.id}/cgi-render`, {
        phase_id: phaseId,
        source_plan_url: sourcePlanUrl.trim(),
        brief: brief.trim() || null,
      });
      setSourcePlanUrl('');
      setBrief('');
      await loadAssets();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Failed to queue CGI render');
    } finally {
      setQueuing(false);
    }
  }

  return (
    <section>
      <h2 style={{ fontSize: 16, fontWeight: 500, letterSpacing: -0.2, marginBottom: 4 }}>CGI renders</h2>
      <p style={{ fontSize: 12, color: 'var(--c-ink-mute)', marginTop: 0, marginBottom: 8 }}>
        Photoreal renders of unbuilt phases. Takes a while.
      </p>

      {assets === null ? (
        <p style={{ fontSize: 13, color: 'var(--c-ink-mute)' }}>Loading…</p>
      ) : assets.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--c-ink-mute)' }}>No renders queued yet.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {assets.map((a) => (
            <li key={a.id} style={{ fontSize: 13 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: CGI_ASSET_STATUS_COLOR[a.status] || 'var(--c-ink-mute)', fontWeight: 500, fontSize: 12.5 }}>
                  {humanize(a.status)}
                </span>
                {a.phase_id ? (
                  <span style={{ color: 'var(--c-ink-mute)' }}>
                    {(phases.find((ph) => ph.id === a.phase_id) || {}).name || 'Phase'}
                  </span>
                ) : null}
                {a.brief ? <span style={{ color: 'var(--c-ink-mute)' }}>· {a.brief}</span> : null}
              </div>
              {a.status === 'failed' && a.error_message ? (
                <p style={{ color: 'var(--c-bad)', fontSize: 12, margin: '2px 0 0' }}>{a.error_message}</p>
              ) : null}
              {a.status === 'complete' && (a.output_asset_urls || []).length > 0 ? (
                <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                  {a.output_asset_urls.map((url, i) => (
                    <a key={url} href={url} target="_blank" rel="noreferrer" style={{ color: 'var(--c-attn)' }}>
                      Render {i + 1}
                    </a>
                  ))}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {phases.length === 0 ? (
        <p style={{ fontSize: 12, color: 'var(--c-bad)' }}>Add a phase to queue a render.</p>
      ) : (
        <form onSubmit={handleQueue} style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 420 }}>
          <select value={phaseId} onChange={(e) => setPhaseId(e.target.value)} style={inputStyle}>
            <option value="">Select a phase…</option>
            {phases.map((ph) => (
              <option key={ph.id} value={ph.id}>{ph.name}</option>
            ))}
          </select>
          <input
            value={sourcePlanUrl}
            onChange={(e) => setSourcePlanUrl(e.target.value)}
            placeholder="Site/floor plan image URL"
            style={inputStyle}
          />
          <input
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            placeholder="Style brief (optional)"
            style={inputStyle}
          />
          <button
            type="submit"
            disabled={queuing || !phaseId || !sourcePlanUrl.trim()}
            className="bx-solid" style={{ ...buttonStyle, cursor: queuing ? 'wait' : 'pointer' }}
          >
            {queuing ? 'Queuing…' : 'Queue render'}
          </button>
        </form>
      )}
      {error ? <p style={{ color: 'var(--c-bad)', fontSize: 13, marginTop: 8 }}>{error}</p> : null}
    </section>
  );
}
